import React, { useState, useRef, useEffect } from "react";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

// ─── Types ───────────────────────────────────────────────────────────────────

interface MindMapNode {
  text: string;
  children: MindMapNode[];
}

interface ApiConfig {
  providerId: string;
  apiKey: string;
  baseUrl: string;
  model: string;
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

const API_CONFIG_KEY = "excalidraw_ai_api_config";

function loadApiConfig(): ApiConfig | null {
  try {
    const raw = localStorage.getItem(API_CONFIG_KEY);
    if (raw) return JSON.parse(raw) as ApiConfig;
  } catch {}
  return null;
}

// ─── AI Call ──────────────────────────────────────────────────────────────────

async function callAI(task: string, config: ApiConfig): Promise<MindMapNode> {
  const systemPrompt = `你是一个任务分解助手。将用户给出的任务分解成思维导图结构。
只返回合法的 JSON，格式如下：
{
  "text": "主任务标题",
  "children": [
    {
      "text": "子任务1",
      "children": [
        { "text": "细节1", "children": [] },
        { "text": "细节2", "children": [] }
      ]
    },
    {
      "text": "子任务2",
      "children": []
    }
  ]
}
要求：
- 主分支 3-6 个
- 每个分支最多 4 个子项
- 每个节点文字简洁（不超过 15 个字）
- 只返回 JSON，不要 markdown 代码块`;

  const url = `${config.baseUrl.replace(/\/$/, "")}/chat/completions`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify({
      model: config.model,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: `请分解这个任务：${task}` },
      ],
      temperature: 0.7,
    }),
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`AI API 错误 ${res.status}: ${errText.slice(0, 200)}`);
  }

  const data = await res.json();
  let content: string = data.choices?.[0]?.message?.content ?? "";

  // Strip markdown code fences if present
  content = content.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();

  try {
    return JSON.parse(content) as MindMapNode;
  } catch {
    throw new Error(`AI 返回了无效 JSON：${content.slice(0, 300)}`);
  }
}

// ─── Layout: radial mind map ──────────────────────────────────────────────────

interface LayoutNode {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  parentId: string | null;
}

const NODE_W = 140;
const NODE_H = 44;
const ROOT_W = 160;
const ROOT_H = 52;
const H_GAP = 80;   // horizontal gap between levels
const V_GAP = 16;   // vertical gap between siblings

let _idCounter = 0;
function newId() {
  return `ai-mm-${Date.now()}-${++_idCounter}`;
}

function measureSubtree(node: MindMapNode): number {
  if (!node.children.length) return NODE_H + V_GAP;
  return node.children.reduce((s, c) => s + measureSubtree(c), 0);
}

function layoutTree(
  node: MindMapNode,
  x: number,
  y: number,
  parentId: string | null,
  nodes: LayoutNode[],
): number /* total height consumed */ {
  const id = newId();
  const isRoot = parentId === null;
  const w = isRoot ? ROOT_W : NODE_W;
  const h = isRoot ? ROOT_H : NODE_H;

  const totalH = isRoot
    ? node.children.reduce((s, c) => s + measureSubtree(c), 0)
    : measureSubtree(node);

  const nodeY = y + totalH / 2 - h / 2;

  nodes.push({ id, text: node.text, x, y: nodeY, width: w, height: h, parentId });

  let childY = y;
  for (const child of node.children) {
    const childH = measureSubtree(child);
    layoutTree(child, x + w + H_GAP, childY, id, nodes);
    childY += childH;
  }

  return totalH;
}

// ─── Excalidraw element builders ─────────────────────────────────────────────

function buildRectElement(ln: LayoutNode, isRoot: boolean, textId: string) {
  return {
    id: ln.id,
    type: "rectangle",
    x: ln.x,
    y: ln.y,
    width: ln.width,
    height: ln.height,
    strokeColor: isRoot ? "#1971c2" : "#364fc7",
    backgroundColor: isRoot ? "#d0ebff" : "#e7f5ff",
    fillStyle: "solid",
    strokeWidth: isRoot ? 2 : 1.5,
    strokeStyle: "solid",
    roughness: 0,
    opacity: 100,
    angle: 0,
    groupIds: [],
    frameId: null,
    roundness: { type: 3, value: 8 },
    seed: Math.floor(Math.random() * 1e9),
    version: 1,
    versionNonce: Math.floor(Math.random() * 1e9),
    isDeleted: false,
    boundElements: [{ type: "text", id: textId }],
    updated: Date.now(),
    link: null,
    locked: false,
  };
}

function buildTextElement(ln: LayoutNode, isRoot: boolean, textId: string) {
  const fontSize = isRoot ? 16 : 13;
  const lineHeight = 1.25;
  const textHeight = fontSize * lineHeight;
  return {
    id: textId,
    type: "text",
    // Center text inside the rect
    x: ln.x,
    y: ln.y + (ln.height - textHeight) / 2,
    width: ln.width,
    height: textHeight,
    text: ln.text,
    fontSize,
    fontFamily: 1,
    textAlign: "center",
    verticalAlign: "middle",
    strokeColor: isRoot ? "#1864ab" : "#1971c2",
    backgroundColor: "transparent",
    fillStyle: "solid",
    strokeWidth: 1,
    strokeStyle: "solid",
    roughness: 0,
    opacity: 100,
    angle: 0,
    groupIds: [],
    frameId: null,
    roundness: null,
    seed: Math.floor(Math.random() * 1e9),
    version: 1,
    versionNonce: Math.floor(Math.random() * 1e9),
    isDeleted: false,
    boundElements: null,
    updated: Date.now(),
    link: null,
    locked: false,
    containerId: ln.id,
    originalText: ln.text,
    lineHeight,
    autoResize: false,
  };
}

function buildArrowElement(
  from: LayoutNode,
  to: LayoutNode,
): object {
  const startX = from.x + from.width;
  const startY = from.y + from.height / 2;
  const endX = to.x;
  const endY = to.y + to.height / 2;
  const midX = (startX + endX) / 2;

  return {
    id: newId(),
    type: "arrow",
    x: startX,
    y: startY,
    width: endX - startX,
    height: endY - startY,
    points: [
      [0, 0],
      [midX - startX, 0],
      [midX - startX, endY - startY],
      [endX - startX, endY - startY],
    ],
    strokeColor: "#74c0fc",
    backgroundColor: "transparent",
    fillStyle: "solid",
    strokeWidth: 1.5,
    strokeStyle: "solid",
    roughness: 0,
    opacity: 80,
    angle: 0,
    groupIds: [],
    frameId: null,
    roundness: { type: 2, value: 16 },
    seed: Math.floor(Math.random() * 1e9),
    version: 1,
    versionNonce: Math.floor(Math.random() * 1e9),
    isDeleted: false,
    boundElements: [],
    updated: Date.now(),
    link: null,
    locked: false,
    startBinding: null,
    endBinding: null,
    lastCommittedPoint: null,
    startArrowhead: null,
    endArrowhead: "arrow",
  };
}

function treeToExcalidrawElements(root: MindMapNode): object[] {
  _idCounter = 0;
  const layoutNodes: LayoutNode[] = [];
  layoutTree(root, 100, 100, null, layoutNodes);

  const idMap = new Map(layoutNodes.map((n) => [n.id, n]));
  const elements: object[] = [];

  for (const ln of layoutNodes) {
    const isRoot = ln.parentId === null;
    const textId = `${ln.id}-text`;
    elements.push(buildRectElement(ln, isRoot, textId));
    elements.push(buildTextElement(ln, isRoot, textId));
    if (ln.parentId) {
      const parent = idMap.get(ln.parentId);
      if (parent) elements.push(buildArrowElement(parent, ln));
    }
  }

  return elements;
}

// ─── Panel Component ──────────────────────────────────────────────────────────

interface Props {
  excalidrawAPI: ExcalidrawImperativeAPI;
}

export const AIMindMapPanel: React.FC<Props> = ({ excalidrawAPI }) => {
  const [task, setTask] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Drag state
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 20, y: 80 });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Dragging handlers
  const onMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === "BUTTON" || (e.target as HTMLElement).tagName === "TEXTAREA") return;
    dragging.current = true;
    dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setPosition({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const handleGenerate = async () => {
    if (!task.trim()) return;

    const config = loadApiConfig();
    if (!config || !config.apiKey) {
      setError("请先在菜单栏「⚙️ AI API 设置」中配置 API Key");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const tree = await callAI(task.trim(), config);
      const elements = treeToExcalidrawElements(tree);

      const existing = excalidrawAPI.getSceneElements();
      excalidrawAPI.updateScene({
        // @ts-ignore
        elements: [...existing, ...elements],
      });

      // Scroll to the new elements
      setTimeout(() => {
        excalidrawAPI.scrollToContent(undefined, { fitToContent: true, animate: true });
      }, 200);

      setSuccess(true);
      setTask("");
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err?.message ?? "生成失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={panelRef}
      onMouseDown={onMouseDown}
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 100,
        width: 280,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
        padding: "14px 16px 16px",
        cursor: "move",
        userSelect: "none",
        border: "1px solid #e9ecef",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 16, marginRight: 6 }}>🧠</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#212529" }}>AI 任务分解</span>
        <span
          style={{
            marginLeft: "auto",
            fontSize: 11,
            background: "#e7f5ff",
            color: "#1971c2",
            borderRadius: 4,
            padding: "1px 6px",
          }}
        >
          Excalidraw
        </span>
      </div>

      {/* Input */}
      <textarea
        value={task}
        onChange={(e) => setTask(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        placeholder="输入你的任务描述，例如：&#10;开发一个电商网站"
        disabled={loading}
        rows={3}
        style={{
          width: "100%",
          padding: "8px 10px",
          border: "1px solid #ced4da",
          borderRadius: 8,
          fontSize: 13,
          fontFamily: "inherit",
          resize: "vertical",
          boxSizing: "border-box",
          outline: "none",
          background: loading ? "#f8f9fa" : "#fff",
          transition: "border-color 0.2s",
        }}
        onFocus={(e) => { e.target.style.borderColor = "#339af0"; }}
        onBlur={(e) => { e.target.style.borderColor = "#ced4da"; }}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleGenerate(); }}
      />

      <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 3, marginBottom: 10 }}>
        Ctrl+Enter 快速生成
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            background: "#fff5f5",
            border: "1px solid #ffc9c9",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 12,
            color: "#c92a2a",
            marginBottom: 10,
          }}
        >
          ⚠️ {error}
        </div>
      )}

      {/* Success */}
      {success && (
        <div
          style={{
            background: "#ebfbee",
            border: "1px solid #b2f2bb",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 12,
            color: "#2f9e44",
            marginBottom: 10,
          }}
        >
          ✅ 思维导图已生成！
        </div>
      )}

      {/* Button */}
      <button
        onClick={handleGenerate}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={!task.trim() || loading}
        style={{
          width: "100%",
          padding: "9px 0",
          background: loading ? "#74c0fc" : !task.trim() ? "#adb5bd" : "#1971c2",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          cursor: !task.trim() || loading ? "not-allowed" : "pointer",
          transition: "background 0.2s",
          letterSpacing: "0.3px",
        }}
      >
        {loading ? "⏳ AI 生成中…" : "✨ 生成思维导图"}
      </button>

      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          color: "#868e96",
          textAlign: "center",
        }}
      >
        需要先配置 API Key（菜单 → ⚙️ AI API 设置）
      </div>
    </div>
  );
};

export default AIMindMapPanel;
